<template>
  <div>
    <div style="margin-bottom: 20px;">
      <Button
        :label="t('Adicionar Origem')"
        icon="pi pi-plus"
        size="small"
        data-testid="origens-add-button"
        @click="handleNew"
      />
    </div>

    <!-- Origens Table -->
    <origens-table
      ref="origensTableRef"
      @edit="handleEdit"
      @delete="handleDelete">
    </origens-table>

    <!-- OrigensModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import OrigensTable from './OrigensTable.vue';
import { eventBus } from '../../eventBus';
// OrigensModal is centralized in ModalManager.vue
import { log } from '../../utils/DebugLogger';

type OrigemId = number | string;

interface OrigemRecord {
  origem_id?: number;
  origem_nome?: string;
  [key: string]: unknown;
}

interface OrigensTableInstance {
  loadData: () => void | Promise<void>;
}

withDefaults(defineProps<{
  translations?: Record<string, unknown>;
}>(), {
  translations: () => ({})
});

// Get i18n composable
const { t } = useI18n();

// Reactive data
const origensTableRef = ref<OrigensTableInstance | null>(null);

// Methods
const loadData = async (): Promise<void> => {
  log('OrigensPage', 'loadData', 'Reloading data');
  if (origensTableRef.value) {
    await origensTableRef.value.loadData();
  }
};

const handleNew = (): void => {
  log('OrigensPage', 'handleNew', 'Opening modal for new origin via eventBus');
  eventBus.emit('modal.origens.open', { action: 'create' });
};

const handleEdit = (origemId?: OrigemId): void => {
  log('OrigensPage', 'handleEdit', 'Editing origin ID via eventBus', origemId);
  if (origemId === undefined) return;
  eventBus.emit('modal.origens.open', { id: origemId, action: 'edit' });
};

const handleDelete = (origemId?: OrigemId, origemData: OrigemRecord | null = null): void => {
  log('OrigensPage', 'handleDelete', 'Deleting origin ID via eventBus', origemId);
  if (origemId === undefined) return;
  eventBus.emit('modal.origens.open', { id: origemId, data: origemData, action: 'delete' });
};

// Lifecycle
onMounted(() => {
  log('OrigensPage', 'mounted', 'Component mounted');

  // Set up eventBus listeners for backward compatibility
  eventBus.on('origem.new', handleNew);
  eventBus.on('origem.edit', handleEdit);
  eventBus.on('origem.delete', handleDelete);
});

onBeforeUnmount(() => {
  log('OrigensPage', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up eventBus listeners
  eventBus.off('origem.new', handleNew);
  eventBus.off('origem.edit', handleEdit);
  eventBus.off('origem.delete', handleDelete);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleNew,
  handleEdit,
  handleDelete
});
</script>

<style scoped>
</style>
